import { routerRef } from "@/lib/routerRef";
import { attachLocationHeader } from "@/lib/geoLocation";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { isEntityScoped, SERVENGINE_SCOPE, useEntityTenantStore } from "@/store/entityTenantStore";
import { useRecordTenantStore } from "@/store/recordTenantStore";
import { useTableFilterBaseStore } from "@/store/tableFilterStore";
import { showErrorAlert } from "@/utils/responseHandler";
import { FORM_NAME_TAKEN } from "@/api/form-builder/formNameTaken";
import axios from "axios";
import { clearAllStores } from "@/utils/authHelpers";
import { saveCurrentLocationForReturn } from "@/utils/postLoginReturn";
import type { CustomAxiosRequestConfig } from "@/types/common.types";

let isLoggingOut = false;

const axiosInstance = axios.create({
  // An empty VITE_BASE_URL (local same-origin proxy mode) must resolve relative
  // endpoint paths like "client/search" against the origin root, not the current
  // page path — otherwise nested routes produce URLs like /work-orders/client/search.
  baseURL: import.meta.env.VITE_BASE_URL || "/",
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const { impersonationSessionId } = useImpersonationStore.getState();
    if (impersonationSessionId) {
      config.headers["impersonation-session-id"] = impersonationSessionId;
    }

    // Acting-entity scoping. The entity a super admin picked in the top-left
    // label menu rides on every request, and the backend rewrites the request's
    // tenant context from it (see backend auth/actingEntity.ts), which narrows
    // every list and every create at once. Sent only when an entity is actually
    // selected: "ServEngine" means no header, so the unscoped view is what you
    // get both when it is chosen and if the header is ever stripped in transit.
    // Never sent alongside impersonation — the backend rejects that pair rather
    // than let one silently win over the other.
    //
    // SERV-3232: a call that names its entity (a merged-view row) wins, then the
    // record a detail route has open, then the pick.
    if (!impersonationSessionId) {
      const actingEntityId =
        (config as CustomAxiosRequestConfig).actingEntityId ??
        useRecordTenantStore.getState().recordTenant ??
        useEntityTenantStore.getState().entityTenant;
      if (isEntityScoped(actingEntityId)) {
        config.headers["acting-entity-id"] = actingEntityId;
      }
    }

    return attachLocationHeader(config);
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;

    // The selected entity no longer resolves (deleted, deactivated, or the id is
    // stale in this browser's localStorage). Drop back to the unscoped ServEngine
    // view rather than leaving every request failing with no way out. Reloading
    // is deliberate: the cached react-query data belongs to a scope that no
    // longer applies, and a reload is the one action guaranteed to drop all of it
    // rather than relying on every call site to refetch.
    //
    // SERV-3232: only when the refused entity is the pick. A row's or an open
    // record's entity says nothing about the pick, so that failure goes to the
    // caller like any other refusal.
    const { entityTenant, setEntityTenant } = useEntityTenantStore.getState();
    if (
      error?.response?.data?.error?.code === "INVALID_ACTING_ENTITY" &&
      error?.config?.headers?.["acting-entity-id"] === entityTenant
    ) {
      setEntityTenant(SERVENGINE_SCOPE);
      showErrorAlert(
        "That entity is no longer available. Switched back to ServEngine.",
        "Entity Unavailable",
      );
      window.location.reload();
      return Promise.reject(error);
    }

    if (status === 401) {
      if (!isLoggingOut) {
        isLoggingOut = true;
        saveCurrentLocationForReturn();
        useTableFilterBaseStore.getState().resetAllFilters();
        clearAllStores();
        showErrorAlert("Please login again", "Session Expired");
        routerRef.current?.navigate({ to: "/login" });
        setTimeout(() => {
          isLoggingOut = false;
        }, 2000);
      }

      return Promise.reject(error);
    }

    // Handle error messages for all other errors
    const errorData = error?.response?.data;
    const errorCode = errorData?.error?.code;

    // Check if the request has disabled error alerts
    const suppressErrorAlert =
      error?.config?.suppressErrorAlert ||
      error?.config?.suppressErrorAlertStatuses?.includes(status) ||
      error?.config?.suppressErrorAlertCodes?.includes(errorCode);

    if (suppressErrorAlert) return Promise.reject(error);

    if (
      errorData &&
      !errorData.success &&
      errorCode !== "POTENTIAL_DUPLICATE_CONTACT" &&
      // Handled by the form builder save flow with a friendlier message.
      errorCode !== "FORM_VISIBILITY_IN_USE" &&
      errorCode !== FORM_NAME_TAKEN
    ) {
      const errorDetails = errorData?.error?.details;

      // Check if details array exists, is an array, and is not empty
      if (Array.isArray(errorDetails) && errorDetails.length > 0) {
        errorDetails.forEach((detail: { field?: string; message: string }) => {
          showErrorAlert(detail.message, "Validation Error");
        });
      } else {
        const errorMessage =
          errorData?.error?.message || errorData?.data?.message || "An error occurred";
        showErrorAlert(errorMessage);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
